import { supabase } from '../lib/supabase';

/**
 * Author Review Service
 * Handles fetching submissions with reviews and decisions for authors
 * Converts between snake_case (database) and camelCase (React)
 */

export const authorReviewService = {
  decisionReleasedStatuses: ['accepted', 'rejected', 'revision_required'],

  isDecisionReleased(status) {
    return this.decisionReleasedStatuses.includes(status);
  },

  /**
   * Get all submissions with reviews for an author
   */
  async getSubmissionsWithReviews(userId) {
    try {
      // Fetch all submissions for the author
      const { data: submissions, error: submissionsError } = await supabase
        ?.from('conference_submissions')
        ?.select(`
          *,
          conferences (id, name, short_name, review_deadline),
          conference_tracks (name)
        `)
        ?.eq('user_id', userId)
        ?.in('status', ['submitted', 'under_review', 'accepted', 'rejected', 'revision_required'])
        ?.order('submitted_at', { ascending: false });

      if (submissionsError) throw submissionsError;

      if (!submissions || submissions?.length === 0) {
        return [];
      }

      // Fetch all reviews for these submissions
      const submissionIds = submissions?.map(s => s?.id) || [];
      const { data: reviews, error: reviewsError } = await supabase
        ?.from('submission_reviews')
        ?.select(`
          *,
          user_profiles (first_name, last_name, institution)
        `)
        ?.in('submission_id', submissionIds);

      if (reviewsError) throw reviewsError;

      // Fetch review criteria for all conferences
      const conferenceIds = [...new Set(submissions?.map(s => s?.conference_id))];
      const { data: criteria, error: criteriaError } = await supabase
        ?.from('review_criteria')
        ?.select('*')
        ?.in('conference_id', conferenceIds)
        ?.order('display_order', { ascending: true });

      if (criteriaError) throw criteriaError;

      // Fetch all review answers
      const { data: answers, error: answersError } = await supabase
        ?.from('review_answers')
        ?.select('*')
        ?.in('submission_id', submissionIds);

      if (answersError) throw answersError;

      // Map submissions with reviews
      const submissionsWithReviews = submissions?.map(sub => {
        const decisionReleased = this.isDecisionReleased(sub?.status);
        const submissionReviews = decisionReleased
          ? reviews?.filter(r => r?.submission_id === sub?.id) || []
          : [];
        const submissionAnswers = decisionReleased
          ? answers?.filter(a => a?.submission_id === sub?.id) || []
          : [];
        const submissionCriteria = criteria?.filter(c => c?.conference_id === sub?.conference_id) || [];

        // Calculate aggregate scores
        const scores = this.calculateAggregateScores(submissionReviews);

        // Map reviews with details
        const reviewsWithDetails = submissionReviews?.map(review => {
          const reviewerAnswers = submissionAnswers?.filter(a => a?.reviewer_id === review?.reviewer_id) || [];
          
          return {
            id: review?.id,
            reviewerId: review?.reviewer_id,
            reviewerName: review?.user_profiles ? `Reviewer ${review?.reviewer_id?.substring(0, 8)}` : 'Anonymous Reviewer',
            overallScore: review?.overall_score,
            originalityScore: review?.originality_score,
            technicalQualityScore: review?.technical_quality_score,
            clarityScore: review?.clarity_score,
            relevanceScore: review?.relevance_score,
            detailedFeedback: review?.detailed_feedback,
            strengths: review?.strengths,
            weaknesses: review?.weaknesses,
            questionsForAuthors: review?.questions_for_authors,
            decision: review?.decision,
            confidenceLevel: review?.confidence_level,
            generalComments: review?.general_comments,
            submittedAt: review?.submitted_at,
            criteriaAnswers: reviewerAnswers?.map(ans => ({
              criteriaId: ans?.criteria_id,
              answerNumeric: ans?.answer_numeric,
              answerBoolean: ans?.answer_boolean,
              answerText: ans?.answer_text
            }))
          };
        });

        return {
          id: sub?.id,
          conferenceId: sub?.conference_id,
          conferenceName: sub?.conferences?.name,
          conferenceAcronym: sub?.conferences?.short_name,
          trackName: sub?.conference_tracks?.name,
          title: sub?.title,
          abstract: sub?.abstract,
          keywords: sub?.keywords || [],
          status: sub?.status,
          decisionReleased,
          submittedAt: sub?.submitted_at,
          reviewDeadline: sub?.conferences?.review_deadline,
          reviewCount: submissionReviews?.length,
          reviews: reviewsWithDetails,
          aggregateScores: scores,
          criteria: submissionCriteria?.map(c => ({
            id: c?.id,
            questionText: c?.question_text,
            questionType: c?.question_type,
            isRequired: c?.is_required
          }))
        };
      });

      return submissionsWithReviews;
    } catch (error) {
      console.error('Get submissions with reviews error:', error);
      throw error;
    }
  },

  /**
   * Calculate aggregate scores from reviews
   */
  calculateAggregateScores(reviews) {
    if (!reviews || reviews?.length === 0) {
      return {
        avgOverall: null,
        avgOriginality: null,
        avgTechnicalQuality: null,
        avgClarity: null,
        avgRelevance: null,
        varianceOverall: null,
        minOverall: null,
        maxOverall: null
      };
    }

    const overallScores = reviews?.map(r => r?.overall_score)?.filter(s => s != null);
    const originalityScores = reviews?.map(r => r?.originality_score)?.filter(s => s != null);
    const technicalScores = reviews?.map(r => r?.technical_quality_score)?.filter(s => s != null);
    const clarityScores = reviews?.map(r => r?.clarity_score)?.filter(s => s != null);
    const relevanceScores = reviews?.map(r => r?.relevance_score)?.filter(s => s != null);

    const avg = (arr) => arr?.length > 0 ? arr?.reduce((a, b) => a + b, 0) / arr?.length : null;
    const variance = (arr) => {
      if (arr?.length < 2) return null;
      const mean = avg(arr);
      return Math.sqrt(arr?.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / arr?.length);
    };

    return {
      avgOverall: avg(overallScores),
      avgOriginality: avg(originalityScores),
      avgTechnicalQuality: avg(technicalScores),
      avgClarity: avg(clarityScores),
      avgRelevance: avg(relevanceScores),
      varianceOverall: variance(overallScores),
      minOverall: overallScores?.length > 0 ? Math.min(...overallScores) : null,
      maxOverall: overallScores?.length > 0 ? Math.max(...overallScores) : null
    };
  },

  /**
   * Get submission by ID with reviews (for detailed view)
   */
  async getSubmissionById(submissionId, userId) {
    try {
      const { data: submission, error: submissionError } = await supabase
        ?.from('conference_submissions')
        ?.select(`
          *,
          conferences (id, name, short_name, review_deadline),
          conference_tracks (name)
        `)
        ?.eq('id', submissionId)
        ?.eq('user_id', userId)
        ?.single();

      if (submissionError) throw submissionError;

      // Fetch reviews
      const { data: reviews, error: reviewsError } = await supabase
        ?.from('submission_reviews')
        ?.select(`
          *,
          user_profiles (first_name, last_name, institution)
        `)
        ?.eq('submission_id', submissionId);

      if (reviewsError) throw reviewsError;

      // Fetch criteria
      const { data: criteria, error: criteriaError } = await supabase
        ?.from('review_criteria')
        ?.select('*')
        ?.eq('conference_id', submission?.conference_id)
        ?.order('display_order', { ascending: true });

      if (criteriaError) throw criteriaError;

      // Fetch answers
      const { data: answers, error: answersError } = await supabase
        ?.from('review_answers')
        ?.select('*')
        ?.eq('submission_id', submissionId);

      if (answersError) throw answersError;

      const decisionReleased = this.isDecisionReleased(submission?.status);
      const visibleReviews = decisionReleased ? reviews || [] : [];
      const visibleAnswers = decisionReleased ? answers || [] : [];
      const scores = this.calculateAggregateScores(visibleReviews);

      const reviewsWithDetails = visibleReviews?.map(review => {
        const reviewerAnswers = visibleAnswers?.filter(a => a?.reviewer_id === review?.reviewer_id) || [];
        
        return {
          id: review?.id,
          reviewerId: review?.reviewer_id,
          reviewerName: `Reviewer ${review?.reviewer_id?.substring(0, 8)}`,
          overallScore: review?.overall_score,
          originalityScore: review?.originality_score,
          technicalQualityScore: review?.technical_quality_score,
          clarityScore: review?.clarity_score,
          relevanceScore: review?.relevance_score,
          detailedFeedback: review?.detailed_feedback,
          strengths: review?.strengths,
          weaknesses: review?.weaknesses,
          questionsForAuthors: review?.questions_for_authors,
          decision: review?.decision,
          confidenceLevel: review?.confidence_level,
          generalComments: review?.general_comments,
          submittedAt: review?.submitted_at,
          criteriaAnswers: reviewerAnswers?.map(ans => ({
            criteriaId: ans?.criteria_id,
            answerNumeric: ans?.answer_numeric,
            answerBoolean: ans?.answer_boolean,
            answerText: ans?.answer_text
          }))
        };
      });

      return {
        id: submission?.id,
        conferenceId: submission?.conference_id,
        conferenceName: submission?.conferences?.name,
        conferenceAcronym: submission?.conferences?.short_name,
        trackName: submission?.conference_tracks?.name,
        title: submission?.title,
        abstract: submission?.abstract,
        keywords: submission?.keywords || [],
        status: submission?.status,
        decisionReleased,
        submittedAt: submission?.submitted_at,
        reviewDeadline: submission?.conferences?.review_deadline,
        reviewCount: visibleReviews?.length,
        reviews: reviewsWithDetails,
        aggregateScores: scores,
        criteria: criteria?.map(c => ({
          id: c?.id,
          questionText: c?.question_text,
          questionType: c?.question_type,
          isRequired: c?.is_required
        }))
      };
    } catch (error) {
      console.error('Get submission by ID error:', error);
      throw error;
    }
  },

  /**
   * Get statistics for author's submissions
   */
  async getAuthorStats(userId) {
    try {
      const { data: submissions, error } = await supabase
        ?.from('conference_submissions')
        ?.select('status')
        ?.eq('user_id', userId);

      if (error) throw error;

      const stats = {
        total: submissions?.length || 0,
        underReview: submissions?.filter(s => s?.status === 'under_review')?.length || 0,
        accepted: submissions?.filter(s => s?.status === 'accepted')?.length || 0,
        rejected: submissions?.filter(s => s?.status === 'rejected')?.length || 0,
        revisionRequired: submissions?.filter(s => s?.status === 'revision_required')?.length || 0
      };

      return stats;
    } catch (error) {
      console.error('Get author stats error:', error);
      throw error;
    }
  }
};

export default authorReviewService;
